<template>
    <v-container fluid class="pa-0">
    <!-- Formulaire -->
    <v-card fluid class="ma-2 pa-3 elevation-0 rounded-xxl micorise-v-card">
        <v-card-title >
            Fiche de santé / Traitement
            <v-spacer></v-spacer>
            <v-chip outlined color="secondary" v-if="item.hea_updated_at">
                <v-icon left>mdi-information-outline</v-icon>Modification: {{ $date(item.hea_updated_at).format('DD MMMM YYYY') }}
            </v-chip>
        </v-card-title>
        <v-form ref="form" v-model="validForm">
        
        <!-- Type et Date -->
        <v-row no-gutters>
            <v-col cols="12" md="6" class="pr-2">
                <v-select outlined dense class="rounded-lg" :items="this.typeListByGroup['HEA']" item-text="typ_name" item-value="typ_id" v-model="item.hea_typ_id" label="Type d'intervention" single-line ></v-select>
            </v-col>
            <v-col cols="12" md="6" class="pl-2">
                <v-text-field :rules="requiredRules" outlined dense class="rounded-lg" label="Date de traitement" v-model="item.hea_treatment_date" type="date"></v-text-field>
            </v-col>
        </v-row>

        <!-- Produit -->
        <v-row no-gutters>
            <v-col cols="12" md="8" class="pr-2">
                <v-text-field :rules="requiredRules" outlined dense class="rounded-lg" label="Produit" v-model="item.hea_product" maxlength="150"></v-text-field>
            </v-col>
            <v-col cols="12" md="4" class="pl-2">
                <v-text-field :rules="requiredRules" outlined dense class="rounded-lg" label="ID Produit" v-model="item.hea_product_id" maxlength="100"></v-text-field>
            </v-col>
        </v-row>

        <!-- Dose et Unité -->
        <v-row no-gutters>
            <v-col cols="12" md="6" class="pr-2">
                <v-text-field outlined dense class="rounded-lg" label="Dose" v-model="item.hea_dose" type="number" step="0.0001"></v-text-field>
            </v-col>
            <v-col cols="12" md="6" class="pl-2">
                <v-select outlined dense class="rounded-lg" :items="this.typeListByGroup['HED']" item-text="typ_name" item-value="typ_id" v-model="item.hea_typ_id_dose" label="Unité de dose" single-line ></v-select>
            </v-col>
        </v-row>

        <!-- Voie administration et Délai d'attente -->
        <v-row no-gutters>
            <v-col cols="12" md="6" class="pr-2">
                <v-select outlined dense class="rounded-lg" :items="this.typeListByGroup['HAD']" item-text="typ_name" item-value="typ_id" v-model="item.hea_typ_id_way" label="Voie d'administration" single-line ></v-select>
            </v-col>
            <v-col cols="12" md="6" class="pl-2">
                <v-text-field outlined dense class="rounded-lg" label="Délai d'attente (jours)" v-model="item.hea_waiting_period" type="number"></v-text-field>
            </v-col>
        </v-row>

        <!-- Libellé -->
        <v-row no-gutters>
            <v-col cols="12">
                <v-text-field :rules="requiredRules" outlined dense class="rounded-lg" label="Libellé" v-model="item.hea_label" maxlength="100"></v-text-field>
            </v-col>
        </v-row>

        <!-- Prescripteur et Intervenant -->
        <v-row no-gutters>
            <v-col cols="12" md="6" class="pr-2">
                <v-text-field :rules="requiredRules" outlined dense class="rounded-lg" label="Prescripteur" v-model="item.hea_order" maxlength="50"></v-text-field>
            </v-col>
            <v-col cols="12" md="6" class="pl-2">
                <AutocompleteMulti 
                    :title="'Intervenant'" :ressource="'users'"
                    :listObject.sync="item.hea_usr_id" :listParent.sync="autoCompleteSource.userList"
                    :fields="{ 
                        index: 'id', text:'middleName', autofocus: true, dense: true,
                        search:'usr_middleName', multiSelect:false, prefetch: true, params:'' 
                    }"/>
            </v-col>
        </v-row>

        <!-- Champs personnalisés -->
        <v-row no-gutters>
            <v-col cols="12">
                <v-textarea outlined dense class="rounded-lg" label="Observations" v-model="item.hea_customfields.observation" rows="2"></v-textarea>
            </v-col>
        </v-row>

        <!-- Animaux concernés (mode multi uniquement en création) -->
        <v-row v-if="!item.hea_id && (!livestock || !livestock.lst_id)" no-gutters>
            <v-col cols="12" class="pr-2 mb-2">
                <v-btn v-if="!manageLivestocks" class="rounded-xl" dense color="success" @click="manageLivestocks = !manageLivestocks">Sélectionner plusieurs animaux</v-btn>
                
                <!-- Autocomplete masqué dès qu'on a scanné des animaux -->
                <AutocompleteMulti v-if="manageLivestocks && !scannedLivestocks.length" ref="autoLivestock"
                        :search-transform="formatLivestockTag"
                        :notfoundClick="createAndSelectLivestock"
                        :title="'Animaux'" :ressource="'livestocks'" :disabled="loading.fetchData"
                        :listObject.sync="livestockIds" :listParent.sync="autoCompleteSource.livestockList" 
                        :fields="{
                            index: 'lst_id', text:'lst_name,lst_tag', autofocus: false, deletablechips: true,
                            search:'lst_tag,lst_name', preprendIcon:'', multiSelect:true, prefetch: true, params:'', resultlimit: 'false', notfound: '-- Créer l\'animal --'
                    }"></AutocompleteMulti>
                
                <v-btn v-if="manageLivestocks && scannedLivestocks.length" text small color="primary" class="mb-2" @click="scannedLivestocks = []">
                    Réafficher la sélection manuelle
                </v-btn>

                <!-- Textarea optimisé pour scannette/clavier rapide -->
                <v-textarea v-if="manageLivestocks" outlined dense class="rounded-lg mt-2" label="Animaux (scanner les tags)" v-model="lstText" rows="3"
                    :loading="loadingScan" :disabled="loadingScan"
                    @input="onScanInput"
                    hint="Scannez les tags un par un ou collez une liste. Les animaux inconnus seront créés automatiquement."></v-textarea>

                <!-- Chips des animaux ajoutés par scan -->
                <div v-if="scannedLivestocks.length" class="mt-2">
                    <div class="text-caption mb-1">{{ scannedLivestocks.length }} animal(s) sélectionné(s) :</div>
                    <v-chip v-for="livestock in scannedLivestocks" :key="livestock.lst_id" close class="ma-1" @click:close="removeScannedLivestock(livestock)">
                        {{ livestock.lst_tag }} <span v-if="livestock.lst_name" class="ml-1">- {{ livestock.lst_name }}</span>
                    </v-chip>
                </div>
            </v-col>
        </v-row>

        </v-form>
        <v-card-actions>
            <v-btn class="rounded-xl" large color="red" v-if="item.hea_id" @click="deleteItem(item)"><v-icon title="Supprimer">mdi-delete</v-icon></v-btn>
            <v-btn class="rounded-xl" large color="grey" :to="{ name: 'livestocks-healthrecords-list' }"><v-icon>mdi-arrow-left-bold-box</v-icon></v-btn>
            <v-spacer></v-spacer>
            <v-btn v-if="!item.hea_id" class="rounded-xl pl-3" large color="blue" :loading="loading.fetchData" @click.native="planItem()">Plannifier</v-btn>
            <v-btn class="rounded-xl pl-3" large color="success" :loading="loading.fetchData" @click.native="closeItem()" >Terminer</v-btn> <!--:disabled="(loading.fetchData || !validForm)"-->
            <v-btn v-if="item.hea_id" class="rounded-xl" large color="success" :loading="loading.fetchData" @click.native="saveItem()" :disabled="(loading.fetchData || !validForm)">Enregistrer</v-btn>
        </v-card-actions>
    </v-card>
    </v-container>
</template>
    
<script>
import { mapState, mapWritableState, mapActions } from 'pinia'
import { useHealthrecordStore } from '@/stores/healthrecord'

import { useTypeStore } from '@/stores/type'
import { useStateStore } from '@/stores/state'
import { useLivestockStore } from '@/stores/livestock'
import AutocompleteMulti from '@/components/ui/AutocompleteMulti'

export default {
    name: 'healthrecords-edit',
    props: ['livestock', 'switchTab', 'itemId'],
    components: { AutocompleteMulti },
    computed: {
        ...mapWritableState(useHealthrecordStore, {item: 'item' }),
        ...mapState(useTypeStore, {typeListByGroup: 'itemListByGroup', typeListById: 'itemListById' }),
        ...mapState(useStateStore, {stateListByGroup: 'itemListByGroup', stateListById: 'itemListById' }),
        ...mapWritableState(useLivestockStore, {itemLivestock: 'item', correctedTag: 'correctedTag' }),
        ...mapState(useLivestockStore, { itemLivestockList: 'itemList' }),
    },
    watch: {
        lstText(newVal) {
            this.onScanInput(newVal)
        },
    },
    data: function () {
        return {
            loading: { fetchData: true },
            validForm: true,
            requiredRules: [ v => !!v || 'Le champ doit être renseigné' ],
            autoCompleteSource: { userList: [], livestockList: [] },
            manageLivestocks: false,
            lstText: '',
            scannedLivestocks: [],
            loadingScan: false,
            isProcessingScan: false,
            notFoundTagsUnique: [],
            debounceTimer: null,
            livestockIds: []
        }
    },
    async mounted () {
        const heaId = (this.itemId) ? this.itemId : this.$route.params.hea_id
        if (heaId && parseInt(heaId) > 0) {
            await this.fetchItem(heaId)
        } else {
           this.initItem()
           this.item.hea_lst_id = (this.livestock && this.livestock.lst_id) ? this.livestock.lst_id : null
           this.item.hea_treatment_date = this.$date().format('YYYY-MM-DD')
           if( !this.item._pivot ) this.item._pivot = { livestocks: [] }
        }
        this.loading.fetchData = false
    },
    methods: {
        ...mapActions(useHealthrecordStore, {fetchItem: 'fetchItem', updateItem: 'updateItem', storeItem: 'storeItem', deleteItem: 'deleteItem', initItem: 'RESET_ITEM'}),
        ...mapActions(useLivestockStore, {fetchLivestockItems: 'fetchItems', storeLivestockItem: 'storeItem', initLivestockItem: 'RESET_ITEM', formatLivestockTag: 'formatTag'}),
        async saveItem () {
            await this.$refs.form.validate()
            if( this.validForm ){
                this.loading.fetchData = true

                // Mode édition : mise à jour classique d'une seule fiche
                if( this.item.hea_id ){
                    await this.updateItem()
                    this.loading.fetchData = false
                    if(this.livestock && this.livestock.lst_id){
                        this.switchTab(1)
                    } else {
                        this.$router.push({ path: '/healthrecords' })
                    }
                    return
                }

                // Mode création : déterminer la liste des animaux concernés
                let animalIds = []
                if( this.livestock && this.livestock.lst_id ){
                    animalIds = [this.livestock.lst_id]
                } else if( this.scannedLivestocks.length ){
                    animalIds = this.scannedLivestocks.map(l => l.lst_id)
                } else if( this.livestockIds && this.livestockIds.length ){
                    animalIds = this.livestockIds
                }

                if( !animalIds.length ){
                    this.loading.fetchData = false
                    return
                }

                // Sauvegarder une fiche par animal avec les mêmes données de formulaire
                const baseItem = { ...this.item, hea_id: null }
                for( const lstId of animalIds ){
                    this.initItem()
                    Object.assign(this.item, baseItem)
                    this.item.hea_lst_id = lstId
                    await this.storeItem()
                }

                this.loading.fetchData = false
                if(this.livestock && this.livestock.lst_id){
                    this.switchTab(1)
                } else {
                    this.$router.push({ path: '/livestocks' })
                }
            }
        },
        async closeItem () {
            this.item.hea_sta_id = 'GEN03'
            this.saveItem()
        },
        async planItem () {
            this.item.hea_sta_id     = 'GEN01'
            this.item.hea_product    = (this.item.hea_product) ? this.item.hea_product : '?'
            this.item.hea_product_id = (this.item.hea_product_id) ? this.item.hea_product_id : '?'
            this.item.hea_label      = (this.item.hea_label) ? this.item.hea_label : '?'
            this.item.hea_order      = (this.item.hea_order) ? this.item.hea_order : '?'
            this.validForm = true
            this.saveItem()
        },
        onScanInput(val) {
            if (this.isProcessingScan) return

            // Normalisation immédiate des caractères parasites envoyés par la scannette
            const normalized = this.normalizeScanInput(val)
            if (normalized !== val) {
                this.lstText = normalized
                return // le watcher rappellera onScanInput avec le texte nettoyé
            }

            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer)
            }

            // Déclenchement rapide si on détecte un séparateur ou un tag complet
            const hasSeparator = /[\n\r\t]/.test(normalized)
            const looksComplete = this.looksLikeCompleteTag(normalized)
            const delay = (hasSeparator || looksComplete) ? 200 : 800

            this.debounceTimer = setTimeout(() => {
                if (normalized.trim().length >= 8) {
                    this.processScan(normalized)
                }
            }, delay)
        },
        normalizeScanInput(val) {
            if (!val) return val
            return val
                .replace(/\u00A0/g, ' ')   // espace insécable
                .replace(/\u200B/g, '')    // zero-width space
                .replace(/\t/g, '\n')      // tabulation -> saut de ligne
                .replace(/\r/g, '\n')      // retour chariot -> saut de ligne
                .replace(/[ ]{2,}/g, ' ')  // espaces multiples
        },
        looksLikeCompleteTag(text) {
            const lines = text.split('\n').filter(l => l.trim())
            return lines.some(line => {
                const trimmed = line.trim()
                return trimmed.length >= 15 && !/\s/.test(trimmed)
            })
        },
        async processScan(rawText) {
            if (!rawText || !rawText.trim() || this.isProcessingScan) return

            this.isProcessingScan = true
            this.loadingScan = true

            try {
                await this.fetchLivestockItems('?per_page=false')
                await this.formatLivestockTag(rawText)

                const corrected = Array.isArray(this.correctedTag)
                    ? this.correctedTag
                    : (this.correctedTag ? [this.correctedTag] : [])

                if (!corrected.length) {
                    this.lstText = ''
                    return
                }

                const assignSet = new Set(corrected)
                const sourceTagsSet = new Set(this.itemLivestockList.map(item => item.lst_tag))

                // Animaux déjà existants correspondant aux tags scannés
                const foundItems = this.itemLivestockList.filter(item => assignSet.has(item.lst_tag))
                const foundIds = foundItems.map(item => item.lst_id)

                // Merge dans le pivot sans doublons
                const currentIds = new Set(this.livestockIds || [])
                foundIds.forEach(id => currentIds.add(id))
                this.livestockIds = [...currentIds]

                // Map des animaux scannés : on part de TOUS les animaux déjà sélectionnés
                const scannedMap = new Map()
                const selectedIds = new Set(this.livestockIds || [])
                this.itemLivestockList
                    .filter(item => selectedIds.has(item.lst_id))
                    .forEach(item => scannedMap.set(item.lst_id, item))

                // Puis on ajoute/écrase avec les nouveaux trouvés
                foundItems.forEach(item => scannedMap.set(item.lst_id, item))

                // Création automatique des animaux inconnus
                const notFoundTags = [...assignSet].filter(tag => !sourceTagsSet.has(tag))
                this.notFoundTagsUnique = [...new Set(notFoundTags)]

                for (const tag of this.notFoundTagsUnique) {
                    const created = await this.createAndSelectLivestock(tag)
                    if (created) {
                        scannedMap.set(created.lst_id, created)
                    }
                }

                this.scannedLivestocks = [...scannedMap.values()]

                if (this.$refs.autoLivestock) {
                    this.$refs.autoLivestock.refreshList(this.livestockIds)
                }

                this.lstText = ''
            } catch (error) {
                console.error('Erreur traitement scan:', error)
            } finally {
                this.loadingScan = false
                this.isProcessingScan = false
            }
        },
        removeScannedLivestock(livestock) {
            this.scannedLivestocks = this.scannedLivestocks.filter(l => l.lst_id !== livestock.lst_id)
            if (this.livestockIds) {
                this.livestockIds = this.livestockIds.filter(id => id !== livestock.lst_id)
            }
            if (this.$refs.autoLivestock) {
                this.$refs.autoLivestock.refreshList(this.livestockIds)
            }
        },
        async createAndSelectLivestock(searchText) {
            if (!searchText || !searchText.trim()) return null

            this.initLivestockItem()
            this.itemLivestock.lst_tag          = searchText
            this.itemLivestock.lst_name         = this.itemLivestock.lst_tag.slice(-5)

            this.itemLivestock.lst_sta_id       = 'LST01'
            this.itemLivestock.lst_typ_id       = 'LST02'
            this.itemLivestock.lst_typ_id_spec  = 'LTT01'
            this.itemLivestock.lst_breed        = '-'
            this.itemLivestock.lst_sex          = 'M'
            this.itemLivestock.lst_birth_date   = this.$date().format('YYYY-MM-DD')
            this.itemLivestock.lst_acquisition  = this.$date().format('YYYY-MM-DD')
            this.itemLivestock.lst_description  = ''
            this.itemLivestock.lst_customfields = {}

            try {
                await this.storeLivestockItem()
                const createdLivestock = { ...this.itemLivestock }
                this.$nextTick(() => {
                    this.livestockIds.push(createdLivestock.lst_id)
                    if (this.$refs.autoLivestock) {
                        this.$refs.autoLivestock.refreshList(this.livestockIds)
                    }
                })
                return createdLivestock
            } catch (error) {
                console.error('Erreur création animal:', error)
                return null
            }
        },
        async formatLivestocks(newVal) {
            await this.fetchLivestockItems('?per_page=false')
            // Use the livestock store's tag formatter (sets this.correctedTag)
            this.formatLivestockTag(newVal)

            // Create a Set of the corrected tags for fast lookup
            const assignSet = new Set(this.correctedTag)
            const sourceTagsSet = new Set(this.itemLivestockList.map(item => item.lst_tag))

            // IDs of livestock whose tag exists in the full list
            const foundIds = this.itemLivestockList
                .filter(item => assignSet.has(item.lst_tag))
                .map(item => item.lst_id)
            // Merge found IDs into the existing pivot (avoid duplicates)
            const currentIds = new Set(this.livestockIds || [])
            foundIds.forEach(id => currentIds.add(id))
            this.livestockIds = [...currentIds]

            // Tags that were NOT found in the full list
            const notFoundTags = this.correctedTag.filter(tag => !sourceTagsSet.has(tag))
            this.notFoundTagsUnique = [...new Set(notFoundTags)]

            // Create each missing livestock
            for (const tag of this.notFoundTagsUnique) {
                await this.createAndSelectLivestock(tag)
            }
            
            this.manageLivestocks = false
            setTimeout( () => {
                this.manageLivestocks = true
            }, 1500 )
            // Clear the textarea after processing
            this.lstText = ''
        },
    }
}
</script>